import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Calendar, CalendarIcon, Loader2 } from 'lucide-react';
import { format, addDays, parse } from 'date-fns';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Calendar as UiCalendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface GoalEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  goalName: string;
  currentDeadline: string;
  onConfirm: (newDeadline: string) => Promise<void>;
  isLoading: boolean;
}

export default function GoalEditDialog({
  open,
  onOpenChange,
  goalName,
  currentDeadline,
  onConfirm,
  isLoading
}: GoalEditDialogProps) {
  const [date, setDate] = useState<Date | undefined>(undefined);

  // Reset state when dialog opens
  React.useEffect(() => {
    if (open && currentDeadline) {
      // Parse the date string into a Date object
      try {
        const parsedDate = parse(currentDeadline, 'yyyy-MM-dd', new Date());
        setDate(parsedDate);
      } catch (error) {
        console.error('Error parsing date:', error);
        setDate(new Date());
      }
    }
  }, [open, currentDeadline]);

  const handleConfirm = async () => {
    if (date) {
      await onConfirm(format(date, 'yyyy-MM-dd'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-500" />
            Update Goal Deadline
          </DialogTitle>
          <DialogDescription>
            Change the deadline for "{goalName}"
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-3">
          <div className="space-y-2">
            <Label className="flex items-center">
              <Calendar className="mr-1 h-4 w-4" />
              New Deadline
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? format(date, 'PPP') : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <UiCalendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  initialFocus
                  disabled={(date) => date < new Date()}
                />
              </PopoverContent>
            </Popover>
            <p className="text-xs text-muted-foreground">
              Choose a new target date for completing this goal
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isLoading || !date}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Updating...
              </>
            ) : (
              'Update Deadline'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}